<template>
  <Combobox
    v-model="selectedId"
    nullable
    @update:modelValue="value => emit('update:modelValue', value)"
    as="div"
    class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2"
  >
    <label
      :for="fieldPath"
      class="block text-sm/6 font-medium text-gray-900 sm:pt-1.5"
    >
      {{ fieldLabel }}
    </label>

    <div class="relative w-full sm:max-w-xs">
      <!-- Input field -->
      <div
        class="relative w-full cursor-default overflow-hidden rounded-md bg-white text-left shadow-sm outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm/6"
      >
        <ComboboxInput
          class="w-full border-none py-1.5 pl-3 pr-10 leading-5 text-gray-900 focus:ring-0 sm:text-sm"
          :display-value="value => optionLabel(value)"
          @change="query = $event.target.value"
          :placeholder="$gettext('Entrer votre recherche')"

        />
        <ComboboxButton
          class="absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <ChevronUpDownIcon
            class="size-5 text-gray-400 sm:size-4"
            aria-hidden="true"
          />
        </ComboboxButton>
      </div>

      <!-- Options list -->
      <Transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ComboboxOptions
          v-if="filteredOptions.length > 0"
          class="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
        >
          <ComboboxOption
            v-for="option in filteredOptions"
            :key="option.value"
            :value="option.value"
            v-slot="{ active, selected }"
          >
            <li
              :class="[
                active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                'relative cursor-default select-none py-2 pl-3 pr-9'
              ]"
            >
              <span
                :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"
              >
                {{ option.label }}
              </span>
              <span
                v-if="selected"
                :class="[
                  active ? 'text-white' : 'text-indigo-600',
                  'absolute inset-y-0 right-0 flex items-center pr-4'
                ]"
              >
                <CheckIcon class="size-5" aria-hidden="true" />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
        <!-- Empty state -->
        <div
          v-else
          class="absolute z-20 mt-1 w-full rounded-md bg-white py-2 px-3 text-sm text-gray-500 shadow ring-1 ring-black/5"
        >
          {{ $gettext("Aucun résultat") }}
        </div>
      </Transition>

      <!-- Error message -->
      <p v-if="errors && errors[fieldPath]" class="text-sm text-red-600">
        {{ errors[fieldPath] }}
      </p>
    </div>
  </Combobox>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/vue";
import { ChevronUpDownIcon } from "@heroicons/vue/16/solid";
import { CheckIcon } from "@heroicons/vue/20/solid";

const props = defineProps([
  "modelValue",
  "fieldPath",
  "fieldAttrs",
  "fieldLabel",
  "errors",
  "options",
]);
const emit = defineEmits(["update:modelValue"]);

const selectedId = ref(props.modelValue); // store just the id
const query = ref("");

// Keep selectedId in sync with parent
watch(
  () => props.modelValue,
  (newVal) => {
    selectedId.value = newVal;
  }
);

// Find label for a given id
const optionLabel = (value) => {
  const opt = props.options.find((o) => o.value === value);
  return opt ? opt.label : "";
};

// Filtering options by label
const filteredOptions = computed(() => {
  if (!query.value) return props.options;
  return props.options.filter((option) => {
    const label = option.label.toLowerCase();
    const q = query.value.toLowerCase();
    return label.includes(q);
  });
});
</script>
